import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/Dialog';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { usePlaybooksApi, PlaybookVersionDetail } from '../apis/playbooks-apis';

interface VersionSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playbookId: string;
  version: number;
}

const VersionSnapshotDialog: React.FC<VersionSnapshotDialogProps> = ({
  open,
  onOpenChange,
  playbookId,
  version,
}) => {
  const [versionDetail, setVersionDetail] = useState<PlaybookVersionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();

  useEffect(() => {
    if (!open || !version) return;

    const fetchVersionDetail = async () => {
      setIsLoading(true);
      setVersionDetail(null);
      try {
        const data = await playbooksApi.getVersion(playbookId, version);
        setVersionDetail(data);
      } catch (error: any) {
        console.error('Error fetching version detail:', error);
        addToast({
          title: "Error",
          description: error.message || 'Failed to load version snapshot',
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersionDetail();
  }, [open, playbookId, version]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Version {version} Snapshot</DialogTitle>
          <DialogDescription>
            Read-only view of the playbook at this version.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Spinner className="w-6 h-6 text-sky-500" />
            <p className="text-sm text-gray-600">Loading version snapshot...</p>
          </div>
        ) : versionDetail ? (
          <div className="space-y-4 py-4">
            {/* Name & Description */}
            <Card className="border-gray-200">
              <CardContent className="p-4 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</label>
                  <p className="text-sm text-gray-900 mt-1">{versionDetail.playbookName}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</label>
                  <p className="text-sm text-gray-900 mt-1">{versionDetail.playbookDescription || 'No description'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Model & Config */}
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Model</label>
                    <p className="text-sm text-gray-900 mt-1">{versionDetail.playbookModel || 'Default'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Chat Type</label>
                    <p className="text-sm text-gray-900 mt-1">{versionDetail.playbookChatType || 'Standard'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</label>
                    <p className="text-sm text-gray-900 mt-1">{versionDetail.playbookProductName || 'Not specified'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tools & Agents counts */}
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {versionDetail.playbookActiveTools?.length || 0} Active Tools
                  </Badge>
                  <Badge className="bg-orange-100 text-orange-700">
                    {versionDetail.playbookApprovalTools?.length || 0} Approval Tools
                  </Badge>
                  <Badge className="bg-violet-100 text-violet-700">
                    {versionDetail.playbookHelperAgents?.length || 0} Agents
                  </Badge>
                  {versionDetail.playbookTags && versionDetail.playbookTags.length > 0 && (
                    <Badge className="bg-sky-100 text-sky-700">
                      {versionDetail.playbookTags.length} Tags
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Workflow */}
            {versionDetail.playbookWorkflow && (
              <Card className="border-gray-200">
                <CardContent className="p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow</label>
                  <div className="bg-gray-50 rounded-lg p-3 mt-2 text-xs font-mono whitespace-pre-wrap border max-h-[200px] overflow-y-auto">
                    {versionDetail.playbookWorkflow}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Special Instructions */}
            {versionDetail.playbookSpecialInstructions && (
              <Card className="border-gray-200">
                <CardContent className="p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Special Instructions</label>
                  <div className="bg-gray-50 rounded-lg p-3 mt-2 text-xs whitespace-pre-wrap border max-h-[200px] overflow-y-auto">
                    {versionDetail.playbookSpecialInstructions}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Metadata */}
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                  <span>Published: {new Date(versionDetail.publishedAt).toLocaleString()}</span>
                  <span>By: {versionDetail.publishedByEmail || versionDetail.publishedBy}</span>
                  {versionDetail.changeNote && (
                    <span>Note: {versionDetail.changeNote}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">Failed to load version snapshot.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionSnapshotDialog;
